import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { initialValues, validationSchema } from './formData';
import {
    ISelect,
    ParamEnum,
    Product,
} from '../../../../../../shared/types/shared';
import { PqrInitialCausality } from '../../../../../../shared/types/modules/pqr';
import { PqrInitialCausalityProps } from './props';
import {
    asyncGetProducts,
    asyncGetProduct,
} from '../../../../../../services/product.service';
import { asyncGetParams } from '../../../../../../services/param.service';
import { asyncGetInitialCausalitys } from '../../../../../../services/initial.causality.service';
import {
    InitialCausalityFilter,
    InitialCausality,
} from '../../../../../../shared/types/typifications/initialCausality';

function buildCausalityKey(
    productCode: string,
    typePqrName: string,
    motiveName: string
): string {
    return `${productCode}|${typePqrName}|${motiveName}`;
}

function getUniqueSubmotivesFromItems(
    items: InitialCausality[],
    field: 'submotive1' | 'submotive2' | 'submotive3' | 'submotive4'
): ISelect[] {
    const seen = new Set<string>();
    const result: ISelect[] = [];
    for (const item of items) {
        const value = item[field];
        if (value == null) continue;
        const id = typeof value === 'string' ? value : value.id;
        const name = typeof value === 'string' ? value : value.name;
        const idStr = String(id);
        if (idStr && !seen.has(idStr)) {
            seen.add(idStr);
            result.push({ id, name: name ?? '' });
        }
    }
    return result;
}

function itemMatchesSubmotive(
    itemValue: string | ISelect | null | undefined,
    selectedId: string
): boolean {
    if (!selectedId || itemValue == null) return false;
    const id = typeof itemValue === 'string' ? itemValue : itemValue.id;
    return String(id) === String(selectedId);
}

function filterItemsBySubmotives(
    items: InitialCausality[],
    submotive1Id?: string,
    submotive2Id?: string,
    submotive3Id?: string
): InitialCausality[] {
    return items.filter((item) => {
        if (
            submotive1Id &&
            !itemMatchesSubmotive(item.submotive1, submotive1Id)
        )
            return false;
        if (
            submotive2Id &&
            !itemMatchesSubmotive(item.submotive2, submotive2Id)
        )
            return false;
        if (
            submotive3Id &&
            !itemMatchesSubmotive(item.submotive3, submotive3Id)
        )
            return false;
        return true;
    });
}

export const usePqrInitialCausality = ({
    data,
    onChangeData,
    onChangeFormik,
    typePqr,
    setIsCausalityFraud,
    setInitialCausalityCurrent,
}: PqrInitialCausalityProps) => {
    const [productData, setProduct] = useState<Product>({});
    const [products, setproducts] = useState<ISelect[]>([]);
    const [motives, setMotives] = useState<ISelect[]>([]);
    const [submotives1, setSubmotives1] = useState<ISelect[]>([]);
    const [submotives2, setSubmotives2] = useState<ISelect[]>([]);
    const [submotives3, setSubmotives3] = useState<ISelect[]>([]);
    const [submotives4, setSubmotives4] = useState<ISelect[]>([]);
    const [typePqrs, setTypePqrs] = useState<ISelect[]>([]);
    const causalidadesCacheRef = useRef<{
        key: string;
        items: InitialCausality[];
    } | null>(null);
    const lastFetchKeyRef = useRef<string | null>(null);

    const onSubmit = (data: PqrInitialCausality) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(
            submotives1.length > 0,
            submotives2.length > 0,
            submotives3.length > 0,
            submotives4.length > 0
        ),
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        if (data) {
            formik?.setFieldValue('product', data?.product || '');
            formik?.setFieldValue('motive', data?.motive || '');

            formik?.setFieldValue('submotive1', data?.submotive1 || '');

            if (data?.submotive1 && data?.submotive2) {
                formik?.setFieldValue('submotive2', data?.submotive2);
            } else {
                formik?.setFieldValue('submotive2', '');
            }

            if (data?.submotive1 && data?.submotive2 && data?.submotive3) {
                formik?.setFieldValue('submotive3', data?.submotive3);
            } else {
                formik?.setFieldValue('submotive3', '');
            }

            if (
                data?.submotive1 &&
                data?.submotive2 &&
                data?.submotive3 &&
                data?.submotive4
            ) {
                formik?.setFieldValue('submotive4', data?.submotive4);
            } else {
                formik?.setFieldValue('submotive4', '');
            }
        }
    }, [data]);

    useEffect(() => {
        asyncGetProducts({ applyPqr: true }).then((items) =>
            setproducts(items)
        );
        asyncGetProduct(formik.values.product).then((res) => {
            if (res) setProduct(res);
        });

        if (typePqr) {
            asyncGetParams(ParamEnum.PQR_MOTIVE, undefined, typePqr).then(
                setMotives
            );
            asyncGetParams(ParamEnum.PQR_TYPE).then(setTypePqrs);
        }
    }, [typePqr, formik.values.product]);

    useEffect(() => {
        const value = formik.values.motive as string;
        if (!value?.trim()) {
            causalidadesCacheRef.current = null;
            lastFetchKeyRef.current = null;
            setSubmotives1([]);
            setSubmotives2([]);
            setSubmotives3([]);
            setSubmotives4([]);
            formik.setFieldValue('submotive1', '');
            formik.setFieldValue('submotive2', '');
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
        }
    }, [formik.values.motive]);

    useEffect(() => {
        const productCode = productData?.code;
        const typePqrName = getNameFromId(typePqr, typePqrs);
        const motiveName = getNameFromId(formik.values.motive, motives);
        if (!productCode || !typePqrName || !motiveName?.trim()) {
            setSubmotives1([]);
            causalidadesCacheRef.current = null;
            return;
        }
        const currentKey = buildCausalityKey(
            productCode,
            typePqrName,
            motiveName
        );
        const cache = causalidadesCacheRef.current;

        if (cache?.key === currentKey) {
            if (cache.items.length === 0) {
                setSubmotives1([]);
                Swal.fire({
                    icon: 'warning',
                    title: 'Sin causalidades configuradas',
                    text: 'No hay causalidades configuradas para la combinación de producto, tipo PQR y motivo seleccionados.',
                });
            } else {
                setSubmotives1(
                    getUniqueSubmotivesFromItems(cache.items, 'submotive1')
                );
            }
            return;
        }

        lastFetchKeyRef.current = currentKey;
        const filters: InitialCausalityFilter = {
            product: productCode,
            typePqr: typePqrName,
            motive: motiveName,
            active: true,
        };
        asyncGetInitialCausalitys(filters, 0, 100).then((res) => {
            if (lastFetchKeyRef.current !== currentKey) return;
            const items = res?.items ?? [];
            causalidadesCacheRef.current = { key: currentKey, items };
            if (items.length === 0) {
                setSubmotives1([]);
                Swal.fire({
                    icon: 'warning',
                    title: 'Sin causalidades configuradas',
                    text: 'No hay causalidades configuradas para la combinación de producto, tipo PQR y motivo seleccionados.',
                });
            } else {
                setSubmotives1(
                    getUniqueSubmotivesFromItems(items, 'submotive1')
                );
            }
        });
    }, [
        formik.values.motive,
        formik.values.product,
        productData?.code,
        typePqr,
        typePqrs,
        motives,
    ]);

    useEffect(() => {
        const productCode = productData?.code;
        const typePqrName = getNameFromId(typePqr, typePqrs);
        const motiveName = getNameFromId(formik.values.motive, motives);
        if (
            !productCode ||
            !typePqrName ||
            !motiveName?.trim() ||
            !formik.values.submotive1
        ) {
            setSubmotives2([]);
            formik.setFieldValue('submotive2', '');
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
            return;
        }
        const cache = causalidadesCacheRef.current;
        const currentKey = buildCausalityKey(
            productCode,
            typePqrName,
            motiveName
        );
        if (!cache || cache.key !== currentKey || cache.items.length === 0) {
            setSubmotives2([]);
            return;
        }
        const filtered = filterItemsBySubmotives(
            cache.items,
            formik.values.submotive1 as string
        );
        setSubmotives2(getUniqueSubmotivesFromItems(filtered, 'submotive2'));
    }, [
        formik.values.motive,
        formik.values.submotive1,
        formik.values.product,
        productData?.code,
        typePqr,
        typePqrs,
        motives,
    ]);

    useEffect(() => {
        const productCode = productData?.code;
        const typePqrName = getNameFromId(typePqr, typePqrs);
        const motiveName = getNameFromId(formik.values.motive, motives);
        if (
            !productCode ||
            !typePqrName ||
            !motiveName?.trim() ||
            !formik.values.submotive1 ||
            !formik.values.submotive2
        ) {
            setSubmotives3([]);
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
            return;
        }
        const cache = causalidadesCacheRef.current;
        const currentKey = buildCausalityKey(
            productCode,
            typePqrName,
            motiveName
        );
        if (!cache || cache.key !== currentKey || cache.items.length === 0) {
            setSubmotives3([]);
            return;
        }
        const filtered = filterItemsBySubmotives(
            cache.items,
            formik.values.submotive1 as string,
            formik.values.submotive2 as string
        );
        setSubmotives3(getUniqueSubmotivesFromItems(filtered, 'submotive3'));
    }, [
        formik.values.motive,
        formik.values.submotive1,
        formik.values.submotive2,
        formik.values.product,
        productData?.code,
        typePqr,
        typePqrs,
        motives,
    ]);

    useEffect(() => {
        const productCode = productData?.code;
        const typePqrName = getNameFromId(typePqr, typePqrs);
        const motiveName = getNameFromId(formik.values.motive, motives);
        if (
            !productCode ||
            !typePqrName ||
            !motiveName?.trim() ||
            !formik.values.submotive1 ||
            !formik.values.submotive2 ||
            !formik.values.submotive3
        ) {
            setSubmotives4([]);
            formik.setFieldValue('submotive4', '');
            return;
        }
        const cache = causalidadesCacheRef.current;
        const currentKey = buildCausalityKey(
            productCode,
            typePqrName,
            motiveName
        );
        if (!cache || cache.key !== currentKey || cache.items.length === 0) {
            setSubmotives4([]);
            return;
        }
        const filtered = filterItemsBySubmotives(
            cache.items,
            formik.values.submotive1 as string,
            formik.values.submotive2 as string,
            formik.values.submotive3 as string
        );
        setSubmotives4(getUniqueSubmotivesFromItems(filtered, 'submotive4'));
    }, [
        formik.values.motive,
        formik.values.submotive1,
        formik.values.submotive2,
        formik.values.submotive3,
        formik.values.product,
        productData?.code,
        typePqr,
        typePqrs,
        motives,
    ]);

    const isCausalityFraud = async (
        data: PqrInitialCausality,
        product: Product
    ) => {
        if (!data.motive || !product || !typePqr) {
            return;
        }

        const motive = getNameFromId(data.motive, motives);
        const filters: InitialCausalityFilter = {
            motive: motive,
            submotive1: getNameFromId(data.submotive1, submotives1),
            submotive2: getNameFromId(data.submotive2, submotives2),
            submotive3: getNameFromId(data.submotive3, submotives3),
            submotive4: getNameFromId(data.submotive4, submotives4),
            product: product?.code,
            typePqr: getNameFromId(typePqr, typePqrs),
        };

        if (filters.motive && filters.product && filters.typePqr) {
            try {
                const res = await asyncGetInitialCausalitys(filters);
                const causality = res?.items;
                if (!causality?.length) {
                    if (setIsCausalityFraud) setIsCausalityFraud(false);
                    return;
                }
                const first = causality[0];
                if (first?.id && setInitialCausalityCurrent) {
                    setInitialCausalityCurrent(first.id);
                }
                const isFraud =
                    first?.typeMotive &&
                    (first.typeMotive as ISelect).code === 'TYPE_MOTIVE_001';
                if (setIsCausalityFraud) setIsCausalityFraud(!!isFraud);
            } catch (error) {
                if (setIsCausalityFraud) setIsCausalityFraud(false);
            }
        }
    };

    const getNameFromId = (
        idOrIds: string | ISelect | (string | ISelect)[] | undefined,
        options: ISelect[]
    ): string => {
        if (!idOrIds) return '';

        if (Array.isArray(idOrIds)) {
            return idOrIds
                .map((item) => {
                    if (typeof item === 'string') {
                        const found = options.find((opt) => opt.id === item);
                        return found?.name ?? item;
                    } else {
                        return item.name;
                    }
                })
                .join(', ');
        }

        if (typeof idOrIds === 'string') {
            const found = options.find((opt) => opt.id === idOrIds);
            return found?.name ?? idOrIds;
        }
        return idOrIds.name;
    };

    useEffect(() => {
        isCausalityFraud(formik.values, productData);
    }, [
        formik.values.motive,
        formik.values.submotive1,
        formik.values.submotive2,
        formik.values.submotive3,
        formik.values.submotive4,
        formik.values.product,
        products,
        motives,
        submotives1,
        submotives2,
        submotives3,
        submotives4,
        productData,
        typePqrs,
    ]);

    return {
        formik,
        products,
        motives,
        submotives1,
        submotives2,
        submotives3,
        submotives4,
    };
};
